import { memo, useEffect, useRef } from 'react';
import { Animated, Platform } from 'react-native';
import { twMerge } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';

const DOT_SIZE = 8;

const ANIMATION_DURATION = 400;

const ANIMATION_DELAY = 150;

/**
 * Creates a pulse animation for a single dot.
 * Animates opacity from 0.3 → 1 → 0.3 for a typing effect.
 * useNativeDriver: false on web; native driver not supported in browser.
 */
function createPulseAnimation(animatedValue: Animated.Value): Animated.CompositeAnimation {
  return Animated.sequence([
    Animated.timing(animatedValue, {
      toValue: 1,
      duration: ANIMATION_DURATION,
      useNativeDriver: Platform.OS !== 'web',
    }),
    Animated.timing(animatedValue, {
      toValue: 0.3,
      duration: ANIMATION_DURATION,
      useNativeDriver: Platform.OS !== 'web',
    }),
  ]);
}

interface TypingIndicatorProps {
  className?: string;
  /**
   * True when the dots sit beside text that already carries the
   * accessible label for this state (e.g. a progress step). Skips the
   * component's own `progressbar` role and label so a screen reader
   * doesn't announce "Loading references" on top of that text.
   */
  decorative?: boolean;
}

/**
 * Animated typing indicator component with 3 pulsing dots.
 * Mimics the iMessage typing indicator effect.
 *
 * The dots animate in sequence: dot1 → dot2 → dot3 → repeat
 * Uses React Native Animated API with native driver for performance.
 */
export const TypingIndicator = memo(function TypingIndicator({
  className,
  decorative,
}: TypingIndicatorProps) {
  const { colors } = useColors();

  const dot1Opacity = useRef(new Animated.Value(0.3)).current;

  const dot2Opacity = useRef(new Animated.Value(0.3)).current;

  const dot3Opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.stagger(ANIMATION_DELAY, [
        createPulseAnimation(dot1Opacity),
        createPulseAnimation(dot2Opacity),
        createPulseAnimation(dot3Opacity),
      ])
    );

    animation.start();

    return () => {
      animation.stop();
    };
  }, [dot1Opacity, dot2Opacity, dot3Opacity]);

  const dotStyle = {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
    backgroundColor: colors.typography[500],
  };

  const a11yProps = decorative
    ? {}
    : getAccessibilityProps({
        label: 'Loading references',
        role: 'progressbar',
      });

  return (
    <HStack
      space="xs"
      className={twMerge('items-center justify-center py-2', className)}
      {...a11yProps}>
      <Animated.View style={[dotStyle, { opacity: dot1Opacity }]} />
      <Animated.View style={[dotStyle, { opacity: dot2Opacity }]} />
      <Animated.View style={[dotStyle, { opacity: dot3Opacity }]} />
    </HStack>
  );
});
